import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Modal,
  IconButton,
  Fade,
  Backdrop,
  styled,
  keyframes,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const floatAnimation = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// New animations for sequential transitions
const imageSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const videoSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const NewspaperContainer = styled(Box)({
  position: "relative",
  minHeight: "50vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "1200px",
  margin: "0 auto",
  width: "100%",
});

const ProfessionalModalHeader = styled(Box)({
  textAlign: "center",
  width: "100%",
  animation: `${slideUp} 0.8s ease-out`,
  zIndex: 10,
  position: "relative",
  background: "linear-gradient(135deg, #006600 0%, #000 50%, #006600 100%)",
  borderRadius: "25px",
  boxShadow: "0 15px 35px rgba(0, 0, 0, 0.1)",
});

const ProfessionalModalTitle = styled(Typography)({
  fontSize: "2rem",
  fontWeight: 700,
  background: "#fff",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
  "@media (max-width: 768px)": {
    fontSize: "1.5rem",
  },
});

// Grid container for 4x2 layout
const ImageGridContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gridTemplateRows: "repeat(2, 1fr)",
  gap: "20px",
  width: "100%",
  maxWidth: "1000px",
  margin: "10px auto",
  // padding: "20px",
  "@media (max-width: 1024px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
    gridTemplateRows: "repeat(4, 1fr)",
  },
  "@media (max-width: 600px)": {
    gridTemplateColumns: "1fr",
    gap: "15px",
  },
});

// Updated FloatingImageCard for grid layout
const FloatingImageCard = styled(Paper)(({ delay }) => ({
  padding: "15px",
  borderRadius: "20px",
  background: "linear-gradient(135deg, #4f8d52ff 0%, #fff  50%, #f8f9fa 100%)",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  width: "100%",
  cursor: "pointer",
  overflow: "hidden",
  animation: `${floatAnimation} 3s ease-in-out ${delay}s infinite, ${zoomIn} 0.6s ease-out ${
    delay * 0.2
  }s both`,
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",

  "&:hover": {
    transform: "scale(1.05) rotate(2deg)",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    borderColor: "#4caf50",
    zIndex: 5,
    animationPlayState: "paused",
  },

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(135deg, #4caf50 0%, #2d8659 100%)",
  },
}));

const FloatingImageContainer = styled(Box)({
  width: "100%",
  height: "150px",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  borderRadius: "15px",
  overflow: "hidden",
  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
  marginBottom: "12px",
  border: "3px solid #e0e0e0",
  transition: "all 0.3s ease",
});

const FloatingCardTitle = styled(Typography)({
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#1b5e20",
  lineHeight: 1.2,
  background: "linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
});

// Updated Modal Content with proper sizing
const ImageModalContent = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: "5%",
  left: "10%",
  right: "10%",
  bottom: "5%",
  backgroundColor: "white",
  borderRadius: "20px",
  padding: theme.spacing(3),
  boxShadow: "0 25px 80px rgba(0, 0, 0, 0.4)",
  outline: "none",
  display: "flex",
  flexDirection: "column",
  zIndex: 1400,
  animation: `${zoomIn} 0.4s ease-out`,
  border: "4px solid #4caf50",
  overflow: "hidden",
}));

// Updated FullImage with sequential animation
const FullImage = styled("img")({
  width: "100%",
  height: "55%", // Increased to 55% for better visibility
  borderRadius: "15px",
  objectFit: "contain",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
  backgroundColor: "#f5f5f5",
  animation: `${imageSlideIn} 0.6s ease-out 0.2s both`,
  opacity: 0,
  transform: "translateY(-30px) scale(0.9)",
});

// Updated Video Container and Player with sequential animation
const VideoContainer = styled(Box)({
  width: "100%",
  height: "40%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  animation: `${videoSlideUp} 0.8s ease-out 0.8s both`,
  opacity: 0,
  transform: "translateY(100%) scale(0.8)",
});

const VideoPlayer = styled("video")({
  width: "100%",
  height: "100%",
  borderRadius: "15px",
  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
  backgroundColor: "#000",
  objectFit: "contain",
});

const ModalTitle = styled(Typography)({
  fontSize: "1.8rem",
  fontWeight: 700,
  background: "linear-gradient(135deg, #1b5e20 0%, #4caf50 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textAlign: "center",
  marginBottom: "8px", // Reduced margin to decrease gap
  flexShrink: 0,
  animation: `${slideUp} 0.5s ease-out 0.1s both`,
  opacity: 0,
});

const ContentContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "calc(100% - 60px)", // Reduced from 80px to decrease gap
  gap: "8px", // Reduced gap to minimize space
  overflow: "hidden",
});

const DigitalMonitoring = () => {
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [selectedVideo, setSelectedVideo] = useState("");
  const [showVideo, setShowVideo] = useState(false);

  const DigitalMonitoringData = [
    {
      id: "dashboard",
      title: "Suthra Punjab Dashboard",
      icon: "📊",
      bgImage: "url('images/DigitalMonitoring/2.jpg')",
      video: "videos/DM/DM1.mp4",
    },
    {
      id: "staff",
      title: "Geo-fenced Distribution of Field Staff",
      icon: "👥",
      bgImage: "url('images/DigitalMonitoring/5.jpg')",
      video: "videos/DM/DM2.mp4",
    },
    {
      id: "fleet",
      title: "Geographical Fleet Distribution",
      icon: "🗺️",
      bgImage: "url('images/DigitalMonitoring/6.jpg')",
      video: "videos/DM/DM3.mp4",
    },
    {
      id: "routes",
      title: "Geo-fenced Vehicle Routes",
      icon: "🚗",
      bgImage: "url('images/DigitalMonitoring/4.jpg')",
      video: "videos/DM/DM4.mp4",
    },
    {
      id: "bins",
      title: "Geo-tagged Waste Bin Clearance",
      icon: "🗑️",
      bgImage: "url('images/DigitalMonitoring/3.jpg')",
      video: "videos/DM/DM5.mp4",
    },
    {
      id: "counting",
      title: "Real Time Vehicle Trip Counting & Waste Collection",
      icon: "📈",
      bgImage: "url('images/DigitalMonitoring/1.jpg')",
      video: "videos/DM/DM6.mp4",
    },
    {
      id: "vtms",
      title: "VTMS Dashboard",
      icon: "📱",
      bgImage: "url('images/DigitalMonitoring/7.jpg')",
      video: "videos/DM/DM7.mp4",
    },
    {
      id: "fms",
      title: "Feedback Management System",
      icon: "🖥️",
      bgImage: "url('images/DigitalMonitoring/8.jpeg')",
      video: "videos/DM/DM8.mp4",
    },
  ];

  const handleImageClick = (bgImage, title, video) => {
    const imageUrl = bgImage.replace(/url\(['"]?([^'")]+)['"]?\)/g, "$1");
    setSelectedImage(imageUrl);
    setSelectedTitle(title);
    setSelectedVideo(video);
    setShowVideo(false);
    setOpenImageModal(true);

    // Show video after image animation completes
    setTimeout(() => {
      setShowVideo(true);
    }, 800);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setShowVideo(false);
    // Small delay to allow animation to complete before resetting
    setTimeout(() => {
      setSelectedImage(null);
      setSelectedTitle("");
      setSelectedVideo("");
    }, 300);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        background: "linear-gradient(180deg, #023e02ff 0%, #000 100%)",
        position: "relative",
        overflow: "hidden",
        padding: "24px",
        borderRadius: "50px",
      }}
    >
      <ProfessionalModalHeader>
        <ProfessionalModalTitle>
          Smart Waste Management Systems
        </ProfessionalModalTitle>
      </ProfessionalModalHeader>

      <NewspaperContainer>
        <ImageGridContainer>
          {DigitalMonitoringData.map((monitoring, index) => (
            <FloatingImageCard
              key={monitoring.id}
              delay={index * 0.1}
              onClick={() =>
                handleImageClick(
                  monitoring.bgImage,
                  monitoring.title,
                  monitoring.video
                )
              }
            >
              <FloatingImageContainer
                sx={{
                  backgroundImage: monitoring.bgImage,
                }}
              />
              <FloatingCardTitle>{monitoring.title}</FloatingCardTitle>
            </FloatingImageCard>
          ))}
        </ImageGridContainer>

        {/* Image Preview Modal */}
        <Modal
          open={openImageModal}
          onClose={handleCloseImageModal}
          slots={{ backdrop: Backdrop }}
          closeAfterTransition
          sx={{
            // This helps prevent ResizeObserver issues
            backdropFilter: "blur(4px)",
          }}
        >
          <Fade in={openImageModal} timeout={400}>
            <ImageModalContent>
              <IconButton
                onClick={handleCloseImageModal}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  color: "#2d8659",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#e8f5e9",
                    transform: "rotate(90deg)",
                  },
                  zIndex: 10,
                }}
              >
                <CloseIcon />
              </IconButton>

              <ModalTitle>{selectedTitle}</ModalTitle>

              <ContentContainer>
                {selectedImage && (
                  <FullImage
                    src={selectedImage}
                    alt={selectedTitle}
                    onLoad={() => {
                      // This can help with ResizeObserver issues
                      window.dispatchEvent(new Event("resize"));
                    }}
                  />
                )}

                {selectedVideo && showVideo && (
                  <VideoContainer>
                    <VideoPlayer
                      controls
                      autoPlay
                      muted
                      onLoadedData={() => {
                        // This can help with ResizeObserver issues
                        window.dispatchEvent(new Event("resize"));
                      }}
                    >
                      <source src={selectedVideo} type="video/mp4" />
                      Your browser does not support the video tag.
                    </VideoPlayer>
                  </VideoContainer>
                )}
              </ContentContainer>
            </ImageModalContent>
          </Fade>
        </Modal>
      </NewspaperContainer>
    </Box>
  );
};

export default DigitalMonitoring;
